/**
 * BRAND — the single source of identity for this app.
 *
 * Every surface reads from here: the splash, the tab bar, the sheets, the accent on every control.
 * Change this file and the whole app changes identity. If you write a hex value anywhere else, the
 * app has started drifting out of its brand.
 *
 * ## This MUST be filled in
 *
 * `applied: false` means the build is still wearing the placeholder identity, and the app shows a
 * visible warning saying so (see `BrandGuard`). That is deliberate. A generated app that ships the
 * default palette looks like every other generated app — the assigned brand language (its colours,
 * type, radii, voice) is what makes it look designed rather than produced.
 *
 * To apply a brand: fill in every field from the assigned brand language, write a real `name`,
 * `tagline` and `voice`, then set `applied: true`. The warning disappears when the work is done.
 */

export interface Brand {
	/** Leave false until a real brand has genuinely been applied — the guard depends on it. */
	applied: boolean;

	/** Product name. Shown on the splash, the home header and the app's native title. */
	name: string;
	/** One line that says what this app is FOR. If a stranger cannot tell from this, rewrite it. */
	tagline: string;

	color: {
		/** Page background. */
		bg: string;
		/** Cards, sheets, the tab bar. */
		surface: string;
		/** Hairlines and dividers. */
		line: string;
		/** Primary text on `bg`. */
		ink: string;
		/** Secondary text. */
		muted: string;
		/** The brand's signature colour: primary buttons, active tab, streak rings. */
		primary: string;
		/** Text/icon on top of `primary`. */
		onPrimary: string;
		/** Positive state — completion, success. */
		success: string;
		/** Attention state — offline, expiring, incomplete. */
		warning: string;
		/** Destructive. */
		danger: string;
	};

	/** Dark-theme overrides. Only what changes; anything omitted inherits from `color`. */
	dark: Partial<Brand['color']>;

	type: {
		/** Display face — headings, numbers, the splash wordmark. */
		display: string;
		/** Body face — everything else. */
		body: string;
	};

	/** Corner radius in px for cards and sheets. Sharp and soft read very differently. */
	radius: number;

	/** How the product speaks. Applies to every string a user reads, including errors. */
	voice: string;
}

export const BRAND: Brand = {
	// PLACEHOLDER IDENTITY — replace all of this, then set applied: true.
	applied: false,

	name: 'Untitled App',
	tagline: 'Placeholder identity — apply the brand',

	color: {
		bg: '#ffffff',
		surface: '#f6f6f7',
		line: '#e5e5e8',
		ink: '#111113',
		muted: '#6b6b74',
		primary: '#111113',
		onPrimary: '#ffffff',
		success: '#1f7a4d',
		warning: '#b45309',
		danger: '#b42318',
	},

	dark: {
		bg: '#0d0d10',
		surface: '#17171b',
		line: '#26262d',
		ink: '#f2f2f4',
		muted: '#9a9aa4',
		primary: '#f2f2f4',
		onPrimary: '#0d0d10',
	},

	type: {
		display: 'system-ui, -apple-system, "Segoe UI", sans-serif',
		body: 'system-ui, -apple-system, "Segoe UI", sans-serif',
	},

	radius: 16,

	voice: 'Plain and warm. Short sentences, second person, no exclamation marks, never patronising.',
};

/** True when this build is still wearing the placeholder identity. */
export const isPlaceholderBrand = (): boolean => !BRAND.applied;

/** #rrggbb -> "H S% L%", the triplet form shadcn/ui tokens are written in. */
function hexToHslTriplet(hex: string): string {
	const clean = hex.replace('#', '');
	const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
	const r = parseInt(full.slice(0, 2), 16) / 255;
	const g = parseInt(full.slice(2, 4), 16) / 255;
	const b = parseInt(full.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
		else if (max === g) h = ((b - r) / d + 2) / 6;
		else h = ((r - g) / d + 4) / 6;
	}

	return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Publish the brand as CSS custom properties.
 *
 * This writes BOTH vocabularies from one source:
 *  - `--brand-*` for anything reading the brand directly.
 *  - the shadcn/ui tokens (`--background`, `--primary`, `--border`, `--radius`, …) so every component
 *    in `components/ui` wears the brand automatically. That mapping is the whole point: the app is
 *    built from the UI kit, and the kit takes its colour from here rather than from hand-written
 *    inline styles on each screen.
 *
 * Called once at startup, and again when the OS colour scheme flips.
 */
export function applyBrandCssVars(): void {
	const root = document.documentElement;
	const dark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
	const palette = { ...BRAND.color, ...(dark ? BRAND.dark : {}) };

	for (const [key, value] of Object.entries(palette)) {
		root.style.setProperty(`--brand-${key}`, value);
	}
	root.style.setProperty('--brand-radius', `${BRAND.radius}px`);
	root.style.setProperty('--brand-display', BRAND.type.display);
	root.style.setProperty('--brand-body', BRAND.type.body);

	// Drive the UI kit from the same palette.
	const token = (name: string, hex: string) => root.style.setProperty(`--${name}`, hexToHslTriplet(hex));
	token('background', palette.bg);
	token('foreground', palette.ink);
	token('card', palette.surface);
	token('card-foreground', palette.ink);
	token('popover', palette.surface);
	token('popover-foreground', palette.ink);
	token('primary', palette.primary);
	token('primary-foreground', palette.onPrimary);
	token('secondary', palette.surface);
	token('secondary-foreground', palette.ink);
	token('muted', palette.surface);
	token('muted-foreground', palette.muted);
	token('accent', palette.surface);
	token('accent-foreground', palette.ink);
	token('destructive', palette.danger);
	token('destructive-foreground', palette.onPrimary);
	token('border', palette.line);
	token('input', palette.line);
	token('ring', palette.primary);
	root.style.setProperty('--radius', `${BRAND.radius}px`);

	// Colour the OS browser chrome to match, so the app does not sit in a mismatched frame.
	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palette.bg);
}
